import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../services/auth';
import api, { getImageUrl } from '../../services/api';
import RichTextEditor from '../Common/RichTextEditor';

interface Page {
  id: number;
  title: string;
  slug: string;
  content: string;
  meta_description?: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

interface MediaFile {
  id: number;
  filename: string;
  original_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  created_at: string;
}

interface Slide {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  image_url: string;
  button_text: string;
  button_link: string;
  is_active: boolean;
  order: number;
  // Hero Section Settings
  slide_width?: string;
  slide_height?: string;
  text_position?: string;
  text_alignment?: string;
  background_overlay?: string;
  animation_type?: string;
  auto_play?: string;
  auto_play_duration?: number;
  custom_classes?: string;
  created_at: string;
  updated_at: string;
}

const CMS: React.FC = () => {
  const [pages, setPages] = useState<Page[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'pages' | 'media' | 'slides' | 'slider-settings' | 'settings'>('pages');
  const [showPageForm, setShowPageForm] = useState(false);
  const [showSlideForm, setShowSlideForm] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [editingPage, setEditingPage] = useState<Page | null>(null);
  const [editingSlide, setEditingSlide] = useState<Slide | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [mediaFilter, setMediaFilter] = useState('all');
  
  // Slider Settings Form State
  const [sliderSettings, setSliderSettings] = useState({
    slider_width: 'full',
    slider_height: 'auto',
    content_position: 'center',
    text_alignment: 'center',
    transition_effect: 'fade',
    transition_duration: 500,
    auto_play: true,
    auto_play_delay: 5000,
    custom_css_classes: '',
    slider_id: 'main-hero-slider'
  });

  const [pageForm, setPageForm] = useState({
    title: '',
    slug: '',
    content: '',
    meta_description: '',
    is_published: false
  });

  const [slideForm, setSlideForm] = useState({
    title: '',
    subtitle: '',
    description: '',
    image_url: '',
    button_text: '',
    button_link: '',
    is_active: true,
    order: 0,
    // Hero Section Settings
    slide_width: 'full',
    slide_height: 'auto',
    text_position: 'center',
    text_alignment: 'center',
    background_overlay: 'none',
    animation_type: 'fade',
    auto_play: 'true',
    auto_play_duration: 5,
    custom_classes: ''
  });

  useEffect(() => {
    fetchData();
    fetchSliderSettings();
  }, []);

  const fetchData = async () => {
    try {
      const [pagesRes, mediaRes, slidesRes] = await Promise.all([
        api.get('/cms/pages'),
        api.get('/cms/media'),
        api.get('/cms/slides')
      ]);
      setPages(pagesRes.data);
      setMediaFiles(mediaRes.data);
      setSlides(slidesRes.data);
    } catch (error) {
      console.error('Error fetching CMS data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSliderSettings = async () => {
    try {
      const response = await api.get('/cms/slider-settings');
      if (response.data) {
        setSliderSettings(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching slider settings:', error);
    }
  };

  const handlePageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await api.put(`/cms/pages/${editingPage.id}`, pageForm);
      } else {
        await api.post('/cms/pages', pageForm);
      }
      setShowPageForm(false);
      setEditingPage(null);
      setPageForm({
        title: '',
        slug: '',
        content: '',
        meta_description: '',
        is_published: false
      });
      fetchData();
    } catch (error) {
      console.error('Error saving page:', error);
    }
  };

  const handleEditPage = (page: Page) => {
    setEditingPage(page);
    setPageForm({
      title: page.title || '',
      slug: page.slug || '',
      content: page.content || '',
      meta_description: page.meta_description || '',
      is_published: page.is_published
    });
    setShowPageForm(true);
  };

  const handleDeletePage = async (pageId: number) => {
    if (window.confirm('Are you sure you want to delete this page?')) {
      try {
        await api.delete(`/cms/pages/${pageId}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting page:', error);
      }
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/cms/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      fetchData();
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleSlideSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingSlide) {
        await api.put(`/cms/slides/${editingSlide.id}`, slideForm);
      } else {
        await api.post('/cms/slides', slideForm);
      }
      setShowSlideForm(false);
      setEditingSlide(null);
      setSlideForm({
        title: '',
        subtitle: '',
        description: '',
        image_url: '',
        button_text: '',
        button_link: '',
        is_active: true,
        order: 0,
        // Hero Section Settings
        slide_width: 'full',
        slide_height: 'auto',
        text_position: 'center',
        text_alignment: 'center',
        background_overlay: 'none',
        animation_type: 'fade',
        auto_play: 'true',
        auto_play_duration: 5,
        custom_classes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error saving slide:', error);
    }
  };

  const handleEditSlide = (slide: Slide) => {
    setEditingSlide(slide);
    setSlideForm({
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      description: slide.description || '',
      image_url: slide.image_url || '',
      button_text: slide.button_text || '',
      button_link: slide.button_link || '',
      is_active: slide.is_active,
      order: slide.order,
      // Hero Section Settings
      slide_width: slide.slide_width || 'full',
      slide_height: slide.slide_height || 'auto',
      text_position: slide.text_position || 'center',
      text_alignment: slide.text_alignment || 'center',
      background_overlay: slide.background_overlay || 'none',
      animation_type: slide.animation_type || 'fade',
      auto_play: slide.auto_play || 'true',
      auto_play_duration: slide.auto_play_duration || 5,
      custom_classes: slide.custom_classes || ''
    });
    setShowSlideForm(true);
  };

  // Media Library Functions
  const openMediaLibrary = () => {
    setShowMediaLibrary(true);
  };

  const closeMediaLibrary = () => {
    setShowMediaLibrary(false);
    setSelectedMedia(null);
  };

  const selectMedia = (media: MediaFile) => {
    setSelectedMedia(media);
  };

  const insertSelectedMedia = () => {
    if (selectedMedia) {
      const imageUrl = getImageUrl(selectedMedia.file_path);
      // Insert media into current form (page or slide)
      if (activeTab === 'pages') {
        setPageForm({...pageForm, content: pageForm.content + `<img src="${imageUrl}" alt="${selectedMedia.original_name}" style="max-width: 100%; height: auto;" />`});
      } else if (activeTab === 'slides') {
        setSlideForm({...slideForm, image_url: imageUrl});
      }
      closeMediaLibrary();
    }
  };

  const filteredMediaFiles = mediaFiles.filter(media => {
    if (mediaFilter === 'all') return true;
    if (mediaFilter === 'images') return media.file_type.startsWith('image/');
    if (mediaFilter === 'documents') return media.file_type.startsWith('application/');
    return true;
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDeleteSlide = async (slideId: number) => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      try {
        await api.delete(`/cms/slides/${slideId}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting slide:', error);
      }
    }
  };

  const handleSliderSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log('Saving slider settings:', sliderSettings);
      
      const response = await api.post('/cms/slider-settings', sliderSettings);
      
      if (response.data) {
        console.log('Slider settings saved successfully:', response.data);
        alert('Slider settings saved successfully!');
      }
    } catch (error: any) {
      console.error('Error saving slider settings:', error);
      const errorMessage = error.response?.data?.message || 'Error saving slider settings';
      alert(errorMessage);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="cms-container">
            <div className="cms-tabs">
              <button 
                className={`tab-btn ${activeTab === 'pages' ? 'active' : ''}`}
                onClick={() => setActiveTab('pages')}
              >
                <i className="fas fa-file-alt"></i> Pages
              </button>
              <button 
                className={`tab-btn ${activeTab === 'media' ? 'active' : ''}`}
                onClick={() => setActiveTab('media')}
              >
                <i className="fas fa-images"></i> Media Library
              </button>
              <button 
                className={`tab-btn ${activeTab === 'slides' ? 'active' : ''}`}
                onClick={() => setActiveTab('slides')}
              >
                <i className="fas fa-sliders-h"></i> Hero Slides
              </button>
              <button 
                className={`tab-btn ${activeTab === 'slider-settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('slider-settings')}
              >
                <i className="fas fa-cog"></i> Slider Settings
              </button>
              <button 
                className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('settings')}
              >
                <i className="fas fa-wrench"></i> Settings
              </button>
            </div>

      {activeTab === 'pages' && (
        <div className="cms-content">
          <div className="content-header">
            <h2>Website Pages</h2>
            <button 
              className="btn-primary"
              onClick={() => setShowPageForm(true)}
            >
              Create New Page
            </button>
          </div>

          {showPageForm && (
            <div className="page-form-modal">
              <div className="modal-content">
                <h3>{editingPage ? 'Edit Page' : 'Create New Page'}</h3>
                <form onSubmit={handlePageSubmit}>
                  <div className="form-group">
                    <label>Page Title</label>
                    <input
                      type="text"
                      value={pageForm.title}
                      onChange={(e) => setPageForm({...pageForm, title: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>URL Slug</label>
                    <input
                      type="text"
                      value={pageForm.slug}
                      onChange={(e) => setPageForm({...pageForm, slug: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Meta Description</label>
                    <textarea
                      value={pageForm.meta_description}
                      onChange={(e) => setPageForm({...pageForm, meta_description: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Page Content</label>
                    <RichTextEditor
                      value={pageForm.content}
                      onChange={(content) => setPageForm({...pageForm, content})}
                      placeholder="Enter page content here..."
                      height="300px"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={pageForm.is_published}
                        onChange={(e) => setPageForm({...pageForm, is_published: e.target.checked})}
                      />
                      Publish Page
                    </label>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      {editingPage ? 'Update Page' : 'Create Page'}
                    </button>
                    <button 
                      type="button" 
                      className="btn-secondary"
                      onClick={() => {
                        setShowPageForm(false);
                        setEditingPage(null);
                        setPageForm({
                          title: '',
                          slug: '',
                          content: '',
                          meta_description: '',
                          is_published: false
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="pages-list">
            {pages.map(page => (
              <div key={page.id} className="page-item">
                <div className="page-info">
                  <h3>{page.title}</h3>
                  <p>/{page.slug}</p>
                  <span className={`status ${page.is_published ? 'published' : 'draft'}`}>
                    {page.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>
                <div className="page-actions">
                  <button 
                    className="btn-edit"
                    onClick={() => handleEditPage(page)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn-delete"
                    onClick={() => handleDeletePage(page.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'media' && (
        <div className="cms-content">
          <div className="content-header">
            <h2>Media Library</h2>
            <div className="upload-area">
              <input
                type="file"
                id="file-upload"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <label htmlFor="file-upload" className="upload-btn">
                Upload File
              </label>
            </div>
          </div>

          <div className="media-grid">
            {mediaFiles.map(file => (
              <div key={file.id} className="media-item">
                <div className="media-preview">
                  {file.file_type.startsWith('image/') ? (
                    <img src={getImageUrl(file.file_path)} alt={file.original_name} />
                  ) : (
                    <div className="file-icon">📄</div>
                  )}
                </div>
                <div className="media-info">
                  <h4>{file.original_name}</h4>
                  <p>{(file.file_size / 1024).toFixed(1)} KB</p>
                  <p>{file.file_type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'slides' && (
        <div className="cms-content">
          <div className="content-header">
            <h2>Hero Slider Management</h2>
            <div className="header-actions">
              <button 
                className="btn-primary"
                onClick={() => setShowSlideForm(true)}
              >
                <i className="fas fa-plus"></i> Add New Slide
              </button>
            </div>
          </div>

          {showSlideForm && (
            <div className="slide-form-modal">
              <div className="modal-content">
                <h3>{editingSlide ? 'Edit Slide' : 'Create New Slide'}</h3>
                <form onSubmit={handleSlideSubmit}>
                  <div className="form-group">
                    <label>Slide Title <span className="optional-text">(Optional)</span></label>
                    <input
                      type="text"
                      value={slideForm.title}
                      onChange={(e) => setSlideForm({...slideForm, title: e.target.value})}
                      placeholder="Enter slide title (optional)"
                    />
                  </div>
                  <div className="form-group">
                    <label>Subtitle <span className="optional-text">(Optional)</span></label>
                    <input
                      type="text"
                      value={slideForm.subtitle}
                      onChange={(e) => setSlideForm({...slideForm, subtitle: e.target.value})}
                      placeholder="Enter subtitle (optional)"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description <span className="optional-text">(Optional)</span></label>
                    <RichTextEditor
                      value={slideForm.description}
                      onChange={(description) => setSlideForm({...slideForm, description})}
                      placeholder="Enter slide description here (optional)..."
                      height="150px"
                    />
                  </div>
                  <div className="form-group">
                    <label>Image URL <span className="required-text">*</span></label>
                    <div className="input-with-button">
                      <input
                        type="url"
                        value={slideForm.image_url}
                        onChange={(e) => setSlideForm({...slideForm, image_url: e.target.value})}
                        required
                      />
                      <button 
                        type="button" 
                        className="btn-browse-media"
                        onClick={openMediaLibrary}
                      >
                        <i className="fas fa-images"></i> Browse Media
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Button Text <span className="optional-text">(Optional)</span></label>
                    <input
                      type="text"
                      value={slideForm.button_text}
                      onChange={(e) => setSlideForm({...slideForm, button_text: e.target.value})}
                      placeholder="Enter button text (optional)"
                    />
                  </div>
                  <div className="form-group">
                    <label>Button Link <span className="optional-text">(Optional)</span></label>
                    <input
                      type="url"
                      value={slideForm.button_link}
                      onChange={(e) => setSlideForm({...slideForm, button_link: e.target.value})}
                      placeholder="Enter button link (optional)"
                    />
                  </div>
                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      value={slideForm.order}
                      onChange={(e) => setSlideForm({...slideForm, order: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={slideForm.is_active}
                        onChange={(e) => setSlideForm({...slideForm, is_active: e.target.checked})}
                      />
                      Active Slide
                    </label>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      {editingSlide ? 'Update Slide' : 'Create Slide'}
                    </button>
                    <button 
                      type="button" 
                      className="btn-secondary"
                      onClick={() => {
                        setShowSlideForm(false);
                        setEditingSlide(null);
                        setSlideForm({
                          title: '',
                          subtitle: '',
                          description: '',
                          image_url: '',
                          button_text: '',
                          button_link: '',
                          is_active: true,
                          order: 0,
                          // Hero Section Settings
                          slide_width: 'full',
                          slide_height: 'auto',
                          text_position: 'center',
                          text_alignment: 'center',
                          background_overlay: 'none',
                          animation_type: 'fade',
                          auto_play: 'true',
                          auto_play_duration: 5,
                          custom_classes: ''
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="slides-list">
            {slides.map(slide => (
              <div key={slide.id} className="slide-item">
                <div className="slide-preview">
                  <img src={getImageUrl(slide.image_url)} alt={slide.title} />
                </div>
                <div className="slide-info">
                  <h3>{slide.title}</h3>
                  <p>{slide.subtitle}</p>
                  <span className={`status ${slide.is_active ? 'active' : 'inactive'}`}>
                    {slide.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="order">Order: {slide.order}</span>
                </div>
                <div className="slide-actions">
                  <button 
                    className="btn-edit"
                    onClick={() => handleEditSlide(slide)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn-delete"
                    onClick={() => handleDeleteSlide(slide.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'slider-settings' && (
        <div className="cms-content full-width">
          <div className="content-header">
            <h2>Hero Slider Settings</h2>
            <p>Configure global settings for the hero slider section</p>
          </div>

          <div className="slider-settings-form">
            <form onSubmit={handleSliderSettingsSubmit}>
              <div className="form-section">
                <h3>Slider Dimensions</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="slider_width">Slider Width</label>
                    <select
                      id="slider_width"
                      value={sliderSettings.slider_width}
                      onChange={(e) => setSliderSettings({...sliderSettings, slider_width: e.target.value})}
                    >
                      <option value="full">Full Width</option>
                      <option value="container">Container Width</option>
                      <option value="custom">Custom Width</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="slider_height">Slider Height</label>
                    <select
                      id="slider_height"
                      value={sliderSettings.slider_height}
                      onChange={(e) => setSliderSettings({...sliderSettings, slider_height: e.target.value})}
                    >
                      <option value="auto">Auto Height</option>
                      <option value="400px">400px</option>
                      <option value="500px">500px</option>
                      <option value="600px">600px</option>
                      <option value="700px">700px</option>
                      <option value="80vh">80% Viewport Height</option>
                      <option value="100vh">Full Viewport Height</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Content Positioning</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="content_position">Content Position</label>
                    <select
                      id="content_position"
                      value={sliderSettings.content_position}
                      onChange={(e) => setSliderSettings({...sliderSettings, content_position: e.target.value})}
                    >
                      <option value="center">Center</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                      <option value="top-left">Top Left</option>
                      <option value="top-right">Top Right</option>
                      <option value="bottom-left">Bottom Left</option>
                      <option value="bottom-right">Bottom Right</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="text_alignment">Text Alignment</label>
                    <select
                      id="text_alignment"
                      value={sliderSettings.text_alignment}
                      onChange={(e) => setSliderSettings({...sliderSettings, text_alignment: e.target.value})}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Animation Settings</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="transition_effect">Transition Effect</label>
                    <select
                      id="transition_effect"
                      value={sliderSettings.transition_effect}
                      onChange={(e) => setSliderSettings({...sliderSettings, transition_effect: e.target.value})}
                    >
                      <option value="fade">Fade</option>
                      <option value="slide">Slide</option>
                      <option value="zoom">Zoom</option>
                      <option value="flip">Flip</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="transition_duration">Transition Duration (ms)</label>
                    <input
                      type="number"
                      id="transition_duration"
                      value={sliderSettings.transition_duration}
                      onChange={(e) => setSliderSettings({...sliderSettings, transition_duration: parseInt(e.target.value)})}
                      min="100"
                      max="5000"
                      step="100"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="auto_play">Auto Play</label>
                    <select
                      id="auto_play"
                      value={sliderSettings.auto_play ? 'true' : 'false'}
                      onChange={(e) => setSliderSettings({...sliderSettings, auto_play: e.target.value === 'true'})}
                    >
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="auto_play_delay">Auto Play Delay (ms)</label>
                    <input
                      type="number"
                      id="auto_play_delay"
                      value={sliderSettings.auto_play_delay}
                      onChange={(e) => setSliderSettings({...sliderSettings, auto_play_delay: parseInt(e.target.value)})}
                      min="1000"
                      max="30000"
                      step="500"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Advanced Settings</h3>
                <div className="form-group">
                  <label htmlFor="custom_css_classes">Custom CSS Classes</label>
                  <input
                    type="text"
                    id="custom_css_classes"
                    value={sliderSettings.custom_css_classes}
                    onChange={(e) => setSliderSettings({...sliderSettings, custom_css_classes: e.target.value})}
                    placeholder="e.g., my-custom-slider hero-banner"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="slider_id">Slider ID</label>
                  <input
                    type="text"
                    id="slider_id"
                    value={sliderSettings.slider_id}
                    onChange={(e) => setSliderSettings({...sliderSettings, slider_id: e.target.value})}
                    placeholder="e.g., main-hero-slider"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  <i className="fas fa-save"></i> Save Slider Settings
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setActiveTab('slides')}
                >
                  <i className="fas fa-arrow-left"></i> Back to Slides
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="cms-content">
          <h2>Website Settings</h2>
          <div className="settings-form">
            <div className="form-group">
              <label>Site Title</label>
              <input type="text" defaultValue="Shopnonagar Portal" />
            </div>
            <div className="form-group">
              <label>Site Description</label>
              <RichTextEditor
                value="Your Digital Community Portal"
                onChange={() => {}}
                placeholder="Enter site description..."
                height="120px"
              />
            </div>
            <div className="form-group">
              <label>Contact Email</label>
              <input type="email" defaultValue="contact@shopnonagar.com" />
            </div>
            <div className="form-group">
              <label>Contact Phone</label>
              <input type="tel" defaultValue="+91-1234567890" />
            </div>
            <button className="btn-primary">Save Settings</button>
          </div>
        </div>
      )}

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <div className="media-library-modal">
          <div className="modal-content" style={{ maxWidth: '900px', height: '80vh' }}>
            <div className="modal-header">
              <h3>Media Library</h3>
              <button 
                className="btn-close" 
                onClick={closeMediaLibrary}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="media-library-content">
              <div className="media-filters">
                <button 
                  className={`filter-btn ${mediaFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setMediaFilter('all')}
                >
                  All Files
                </button>
                <button 
                  className={`filter-btn ${mediaFilter === 'images' ? 'active' : ''}`}
                  onClick={() => setMediaFilter('images')}
                >
                  Images
                </button>
                <button 
                  className={`filter-btn ${mediaFilter === 'documents' ? 'active' : ''}`}
                  onClick={() => setMediaFilter('documents')}
                >
                  Documents
                </button>
              </div>

              <div className="media-grid">
                {filteredMediaFiles.map((media) => (
                  <div 
                    key={media.id} 
                    className={`media-item ${selectedMedia?.id === media.id ? 'selected' : ''}`}
                    onClick={() => selectMedia(media)}
                  >
                    {media.file_type.startsWith('image/') ? (
                      <img 
                        src={getImageUrl(media.file_path)} 
                        alt={media.original_name}
                        className="media-preview"
                      />
                    ) : (
                      <div className="media-icon">
                        <i className="fas fa-file"></i>
                      </div>
                    )}
                    <div className="media-info">
                      <div className="media-name">{media.original_name}</div>
                      <div className="media-size">{formatFileSize(media.file_size)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {selectedMedia && (
                <div className="media-selection">
                  <div className="selected-media-info">
                    <strong>Selected:</strong> {selectedMedia.original_name}
                  </div>
                  <div className="media-actions">
                    <button 
                      className="btn-secondary" 
                      onClick={closeMediaLibrary}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn-primary" 
                      onClick={insertSelectedMedia}
                    >
                      Insert Media
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>


        </div>
      )}
    </div>
  );
};

export default CMS;
